import { useState, type FormEvent } from 'react';
import { Button, Input, AlertBanner, LogoMark } from '@assist/ui';
import { renderProblem } from '@assist/i18n';
import { endpoints, ApiError } from '../state/api.js';
import { useSession } from '../state/session.js';
import { Icon } from '../app/Icon.js';

/**
 * Layar Sign In — Figma `20:2` · M01 · rute `/sign-in` · izin publik.
 *
 * Kondisi yang wajib dibangun (peta §5.1), semuanya ada di sini:
 *   baku · sedang mengirim · kredensial salah (galat DI BAWAH MEDAN, bukan toast)
 *   · akun terkunci setelah 5 percobaan · tenant nonaktif
 *   · slot logo klien kosong memakai tanda ASSIST.
 *
 * Yang membuat "galat di bawah medan" mungkin adalah `Problem.field_errors` di
 * kontrak API dan `renderProblem()` yang menerjemahkan `{code, params}` menjadi
 * kalimat dalam bahasa pengguna — server tidak pernah mengirim prosa.
 */
/**
 * Tautan pemberitahuan privasi, dengan slug organisasi bila sudah diketik.
 *
 * Slug ikut supaya orang yang sudah mengetik organisasinya membaca teks TENANT-nya
 * — bila tenant itu menerbitkan versinya sendiri — alih-alih teks bawaan PQM.
 * Tanpa slug, yang tampil adalah teks bawaan, dan itu jawaban yang benar untuk
 * seseorang yang belum menyebut organisasinya.
 */
function privacyHref(tenantSlug: string): string {
  const slug = tenantSlug.trim();
  return slug === '' ? '/privacy-notice' : `/privacy-notice?tenant_slug=${encodeURIComponent(slug)}`;
}

export function SignIn() {
  const { t, translator, locale, setLocale, refresh } = useSession();
  const [tenantSlug, setTenantSlug] = useState('');
  const [identity, setIdentity] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<ApiError | null>(null);
  /**
   * UAT D-02. `forgotSent` dulu satu-satunya hal yang dilakukan tombol "Lupa kata
   * sandi?": ia menyetel penanda ini, layar menampilkan "tautan sekali pakai sedang
   * dikirim", dan NOL permintaan jaringan terkirim. Rute
   * `POST /v1/auth/forgot-password` ada dan berjalan; ia tidak pernah dipanggil,
   * dan layar tidak pernah menanyakan surel mana yang dimaksud.
   *
   * Sekarang ada tiga keadaan, bukan dua: tertutup, formulir terbuka, dan
   * permintaan tercatat. Yang ketiga mengatakan APA YANG SESUNGGUHNYA TERJADI —
   * pengiriman surel belum dibangun di versi ini, jadi layar tidak menjanjikannya.
   */
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotDone, setForgotDone] = useState(false);
  const [forgotBusy, setForgotBusy] = useState(false);

  // Galat kredensial dan penguncian ditempatkan berbeda: yang pertama di bawah
  // medan, yang kedua sebagai spanduk, karena ia bukan tentang apa yang diketik.
  const code = problem?.problem.code ?? null;
  const isLockout = code === 'ACCOUNT_LOCKED';
  const isTenantInactive = code === 'TENANT_INACTIVE';
  const fieldError =
    problem && !isLockout && !isTenantInactive ? renderProblem(translator, problem.problem) : null;

  /**
   * Mengirim permintaan pemulihan yang SUNGGUHAN.
   *
   * Jawaban server selalu sama, ada atau tidak akunnya (`{ok:true}`), dan itu
   * disengaja — endpoint ini tidak boleh menjadi oracle keberadaan akun. Karena
   * itu layar ini juga tidak dapat, dan tidak boleh, mengatakan "akunnya ada".
   * Yang dapat ia katakan dengan jujur adalah bahwa permintaannya tercatat.
   */
  async function onForgotSubmit(event: FormEvent) {
    event.preventDefault();
    setForgotBusy(true);
    setProblem(null);
    try {
      await endpoints.forgotPassword({
        tenant_slug: tenantSlug.trim() === '' ? null : tenantSlug.trim(),
        identity: identity.trim(),
      });
      setForgotOpen(false);
      setForgotDone(true);
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setForgotBusy(false);
    }
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setProblem(null);
    try {
      await endpoints.signIn({
        tenant_slug: tenantSlug.trim() === '' ? null : tenantSlug.trim(),
        identity: identity.trim(),
        password,
      });
      // Pengarahan setelah masuk ditentukan peran, dan perannya datang dari
      // sesi — bukan dari apa pun yang dipilih layar ini (peta §5.1).
      await refresh();
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  return (
    /**
     * AUDIT L-01 (BERAT) — penyimpangan paling jauh di seluruh proyek: bukan detail
     * yang meleset, melainkan DUA LAYAR YANG BERBEDA.
     *
     * Figma `20:2` / `72:1864` merancang layar terbelah 620 / 820: panel merek navy
     * selebar 620 px — 43 % layar — berisi lockup PQM, tanda ASSIST 56 px, tagline, dan
     * tiga statistik `1987 · 2.100+ · 900+`; lalu formulir 420 px di kolom kanan. Kode
     * membuat SATU KARTU 420 px di tengah latar abu, dan panel mereknya tidak ada sama
     * sekali.
     *
     * Geometri di bawah diambil dari metadata frame, bukan diperkirakan:
     *   panel 620 · kolom kanan 820 · formulir 420 pada x=200, y=183,5
     *   lockup PQM pada 64/72 · lockup ASSIST pada 64/366,5 · statistik pada 64/788
     */
    <div className="signin">
      {/*
        PANEL MEREK. Ia `aria-hidden` sebagian dan TIDAK memuat satu pun kendali:
        seluruh isinya dekoratif kecuali namanya. Pembaca layar karena itu mendarat
        langsung di judul formulir, dan pengguna papan ketik tidak menempuh sembilan
        perhentian sebelum medan pertamanya.
      */}
      <aside className="signin__brand">
        <div className="signin__brand-motif" aria-hidden="true">
          <span className="signin__brand-arc signin__brand-arc--bottom" />
          <span className="signin__brand-arc signin__brand-arc--top" />
          <span className="signin__brand-tab signin__brand-tab--s" />
          <span className="signin__brand-tab signin__brand-tab--l" />
          <span className="signin__brand-tab signin__brand-tab--l" />
          <span className="signin__brand-tab signin__brand-tab--s" />
        </div>

        <div className="signin__brand-org">
          {/*
            SLOT LOGO PQM `54:54` — DIISI, dan itu penyimpangan yang disengaja dari
            frame-nya.

            Figma menggambarnya sebagai kotak bergaris putus-putus kosong, dan namanya
            harfiah "slot/logo PQM": ia placeholder, bukan keputusan bahwa tidak ada
            logo. Kode menyalin placeholder-nya dengan setia selama dua gelombang, dan
            hasilnya adalah kotak kosong di samping tulisan PQM CONSULTANTS di muka
            setiap orang yang masuk ke https://assist.co.id — cacat produksi yang
            dilaporkan pemilik proyek.

            Berkasnya selalu ada di `assets/brand/pqm-mark.svg`. Yang tidak ada adalah
            jalan baginya sampai ke `dist/`: konsol ini tidak punya `publicDir` sama
            sekali, sehingga Vite tidak pernah mengeluarkan satu pun aset merek. Lihat
            catatan panjangnya di `apps/console/vite.config.ts`.

            `alt` DIISI, bukan `aria-hidden`: ia satu-satunya tempat nama perusahaan
            muncul sebagai gambar, dan teks "PQM CONSULTANTS" di sebelahnya adalah
            tulisan yang berbeda — pembaca layar yang melewatkan keduanya melewatkan
            pemilik perangkat lunaknya.
          */}
          <img
            className="signin__brand-logo-slot"
            src="/pqm-mark.svg"
            alt={t('signIn.brand.logoAlt')}
            width={48}
            height={31}
          />
          <span className="signin__brand-org-text">
            <strong>{t('signIn.brand.org')}</strong>
            <small>{t('signIn.brand.orgTagline')}</small>
          </span>
        </div>

        <div className="signin__brand-lockup">
          <div className="signin__brand-wordmark">
            <LogoMark size={56} inverse />
            <span className="signin__brand-name">{t('app.name')}</span>
            <span className="signin__brand-by">{t('signIn.brand.by')}</span>
          </div>
          <p className="signin__brand-tagline">{t('signIn.brand.tagline')}</p>
        </div>

        <dl className="signin__brand-stats">
          <div>
            <dt>{t('signIn.brand.stat.foundedValue')}</dt>
            <dd>{t('signIn.brand.stat.foundedLabel')}</dd>
          </div>
          <div>
            <dt>{t('signIn.brand.stat.clientsValue')}</dt>
            <dd>{t('signIn.brand.stat.clientsLabel')}</dd>
          </div>
          <div>
            <dt>{t('signIn.brand.stat.projectsValue')}</dt>
            <dd>{t('signIn.brand.stat.projectsLabel')}</dd>
          </div>
        </dl>
      </aside>

      <div className="signin__panel">
        {/*
          JUDUL DUA WARNA, dari DUA kunci katalog — tidak pernah disambung dari satu
          string. Urutan katanya membalik antarbahasa di layar lain (Dasbor: `Operations`
          + `Dashboard` versus `Dasbor` + `Operasional`), dan sebuah judul yang dirakit
          dengan menyambung memaksa satu urutan pada kedua bahasa (audit §5).
        */}
        <h1 className="signin__title">
          {t('signIn.title')} <span className="signin__title-accent">{t('signIn.titleAccent')}</span>
        </h1>
        <p className="signin__subtitle">{t('signIn.subtitle')}</p>

        {isLockout ? (
          <AlertBanner
            kind="danger"
            signalWord={t('signal.danger')}
            title={renderProblem(translator, problem!.problem)}
          />
        ) : null}
        {isTenantInactive ? (
          <AlertBanner
            kind="warning"
            signalWord={t('signal.warning')}
            title={renderProblem(translator, problem!.problem)}
          />
        ) : null}
        {forgotDone ? (
          <AlertBanner
            kind="information"
            signalWord={t('signal.information')}
            title={t('signIn.forgotPassword.recorded')}
          />
        ) : null}

        {forgotOpen ? (
          <form className="signin__form" onSubmit={onForgotSubmit} noValidate>
            <h2 className="signin__subtitle">{t('signIn.forgotPassword.title')}</h2>
            <p className="signin__subtitle">{t('signIn.forgotPassword.body')}</p>
            <Input
              label={t('signIn.tenantSlug')}
              hint={t('signIn.tenantSlug.hint')}
              value={tenantSlug}
              autoComplete="organization"
              onChange={(e) => setTenantSlug(e.target.value)}
            />
            <Input
              label={t('signIn.identity')}
              value={identity}
              autoComplete="username"
              required
              onChange={(e) => setIdentity(e.target.value)}
            />
            <Button
              variant="primary"
              htmlType="submit"
              busy={forgotBusy}
              fullWidth
              disabled={identity.trim() === ''}
            >
              {t('signIn.forgotPassword.submit')}
            </Button>
            <Button variant="text" onClick={() => setForgotOpen(false)}>
              {t('signIn.forgotPassword.cancel')}
            </Button>
          </form>
        ) : (
        <form className="signin__form" onSubmit={onSubmit} noValidate>
          <Input
            label={t('signIn.tenantSlug')}
            hint={t('signIn.tenantSlug.hint')}
            value={tenantSlug}
            autoComplete="organization"
            onChange={(e) => setTenantSlug(e.target.value)}
          />
          <Input
            label={t('signIn.identity')}
            value={identity}
            autoComplete="username"
            required
            onChange={(e) => setIdentity(e.target.value)}
          />
          <Input
            label={t('signIn.password')}
            inputType="password"
            value={password}
            autoComplete="current-password"
            required
            error={fieldError}
            onChange={(e) => setPassword(e.target.value)}
          />

          {/*
            BARIS BANTU SEBARIS — Figma `20:51`: kotak centang "Remember this device" di
            kiri dan tautan "Forgot password?" di kanan, KEDUANYA di atas tombol. Kode
            menaruh tautannya sendirian di bawah tombol.

            Kotak centang "ingat perangkat ini" TIDAK dibangun sebagai kendali yang
            bekerja, dan ia sengaja TIDAK dipalsukan: umur sesi ditentukan server, dan
            sebuah kotak centang yang tidak mengubah apa pun adalah kendali mati — kelas
            cacat yang sama dengan kotak cari yang tidak mencari (D-02). Ia akan lahir
            bersama rutenya. Yang dipindahkan sekarang hanyalah tautan lupa kata sandi,
            ke tempat yang Figma tetapkan.
          */}
          <div className="signin__help-row">
            <span />
            <Button
              variant="text"
              onClick={() => {
                setForgotDone(false);
                setForgotOpen(true);
              }}
            >
              {t('signIn.forgotPassword')}
            </Button>
          </div>

          <Button variant="primary" htmlType="submit" busy={busy} fullWidth>
            {busy ? t('signIn.submitting') : t('signIn.submit')}
          </Button>

          {/* Pemisah `atau` + tombol sekunder SSO — Figma `20:58` dan `20:62`. */}
          <div className="signin__or" aria-hidden="true">
            <span>{t('signIn.or')}</span>
          </div>
          {/*
            SSO ADA DI FIGMA (`20:62`) DAN TIDAK ADA DI SISTEM: nol rute dari 156 operasi
            kontrak menyentuh penyedia identitas mana pun, dan tidak ada satu pun
            konfigurasi OIDC/SAML di seluruh repositori.

            BENTUK SEBELUMNYA JUGA SALAH, dan itu perlu dicatat alih-alih dianggap sudah
            beres: tombolnya tampak AKTIF, dan keterangannya baru muncul SESUDAH
            ditekan. Sebuah kendali yang mengumumkan dirinya tidak bekerja hanya kepada
            orang yang sudah mencobanya adalah kelas cacat yang sama dengan tombol mati
            tanpa penjelasan — orang menempuh langkahnya lebih dulu, lalu diberi tahu
            bahwa langkah itu tidak ada.

            Sekarang: tombolnya DIMATIKAN, dan kalimat yang mengatakan mengapa BERDIRI DI
            SEBELAHNYA tanpa ditekan lebih dulu, lengkap dengan apa yang harus dilakukan
            sebagai gantinya. `aria-describedby` menyambungkan keduanya, sehingga pembaca
            layar yang menemui tombol mati langsung mendengar sebabnya alih-alih
            melewatinya sebagai kendali yang tidak dapat dijelaskan.

            Ia TIDAK dihapus: Figma merancangnya, produknya akan memilikinya, dan sebuah
            layar yang menghilangkan bagian rancangan tanpa jejak membuat pertanyaan
            "apakah SSO sudah ada" tidak dapat dijawab dari layarnya sendiri.
          */}
          <Button
            variant="secondary"
            fullWidth
            disabled
            aria-describedby="signin-sso-note"
          >
            {t('signIn.sso')}
          </Button>
          <p className="signin__sso-note" id="signin-sso-note">
            {t('signIn.sso.unavailable')}
          </p>
        </form>
        )}

        {/* Pengalih bahasa. Bentuk sebelumnya adalah tulisan MATI di sebelah
            `<button hidden aria-hidden="true">` yang tidak menampilkan apa pun dan
            tidak melakukan apa pun (UAT D-05). Ia masih dibawa ke gelombang
            berikutnya sebagai keputusan bahasa baku; yang dihapus di sini adalah
            tombol tersembunyinya, karena kendali yang tidak dapat dilihat maupun
            difokuskan bukan kendali, dan keberadaannya membuat pembaca layar
            menghitung sesuatu yang tidak ada. */}
        {/* Baris legal Figma `20:64` — kedaulatan data, 11 px `ink/50`. Ia BUKAN
            pengganti pemberitahuan privasi; keduanya menjawab pertanyaan berbeda. */}
        <p className="signin__legal">{t('signIn.legal')}</p>

        {/*
          TAUTAN PEMBERITAHUAN PRIVASI DI LAYAR MASUK.
          
          Di sinilah orang berada sebelum punya sesi, dan UU 27/2022 Pasal 21
          menuntut pemberitahuan SEBELUM pemrosesan — bukan sesudah. Layar ini
          sendiri sudah memproses data pribadi: setiap percobaan masuk mencatat
          alamat IP, user agent, dan pengenal perangkat, berhasil maupun gagal.
          
          Ia `<a href>` biasa dan bukan tombol yang membuka dialog, karena sebuah
          pemberitahuan privasi harus dapat dibuka di tab baru, disimpan, dan
          dikirim kepada orang lain.
        */}
        <p className="signin__privacy">
          <a className="link" href={privacyHref(tenantSlug)}>
            {t('privacy.notice.link')}
          </a>
        </p>
      </div>

      {/* Pengalih bahasa pil di sudut kanan atas — Figma `70:1444` (73x34, ikon globe
          16 px + label + chevron). Bentuk sebelumnya adalah TULISAN MATI di dalam kartu
          (UAT D-05): ia menyatakan bahasanya dan tidak dapat mengubahnya. */}
      <div className="signin__lang" role="group" aria-label={t('app.language')}>
        <span className="signin__lang-globe" aria-hidden="true">
          <Icon name="globe" size={16} />
        </span>
        <button
          type="button"
          className={locale === 'en-US' ? 'is-active' : ''}
          aria-pressed={locale === 'en-US'}
          onClick={() => void setLocale('en-US')}
        >
          EN
        </button>
        <button
          type="button"
          className={locale === 'id-ID' ? 'is-active' : ''}
          aria-pressed={locale === 'id-ID'}
          onClick={() => void setLocale('id-ID')}
        >
          ID
        </button>
      </div>
    </div>
  );
}
